import * as SliderPrimitive from "@radix-ui/react-slider";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InfoTip } from "@/components/app/Common";

const RangeSlider = ({ value, onChange, testId }) => (
  <SliderPrimitive.Root value={value} onValueChange={onChange} min={0} max={100} step={5} minStepsBetweenThumbs={1}
    className="relative flex h-5 w-full touch-none select-none items-center" data-testid={testId}>
    <SliderPrimitive.Track className="relative h-1.5 w-full grow rounded-full bg-brand-tint">
      <SliderPrimitive.Range className="absolute h-full rounded-full bg-brand" />
    </SliderPrimitive.Track>
    {value.map((_, i) => (
      <SliderPrimitive.Thumb key={i} aria-label={i ? "Maximum" : "Minimum"}
        className="block h-4 w-4 rounded-full border-2 border-brand bg-white transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30" />
    ))}
  </SliderPrimitive.Root>
);

export function TraitsEditor({ traits = [], onChange }) {
  const update = (i, patch) => onChange(traits.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  return (
    <div className="space-y-3" data-testid="traits-editor">
      <p className="flex items-center gap-1.5 text-xs text-ink-muted">
        Target range on a 0–100 scale
        <InfoTip text="The ideal profile describes the personality and work traits that suit this role. Candidates inside the range score best. Importance controls how much each trait counts." />
      </p>
      {traits.map((t, i) => (
        <div key={i} className="grid grid-cols-12 items-center gap-3" data-testid={`trait-row-${i}`}>
          <Input value={t.trait} onChange={(e) => update(i, { trait: e.target.value })} className="col-span-4 h-8 text-sm" data-testid={`trait-name-${i}`} />
          <div className="col-span-4"><RangeSlider value={[t.min, t.max]} onChange={([min, max]) => update(i, { min, max })} testId={`trait-range-${i}`} /></div>
          <span className="col-span-1 text-xs tabular-nums text-ink-secondary">{t.min}–{t.max}</span>
          <select value={t.importance} onChange={(e) => update(i, { importance: e.target.value })}
            className="col-span-2 h-8 rounded-lg border border-line bg-white px-2 text-sm" data-testid={`trait-importance-${i}`}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
          <button className="col-span-1 justify-self-end text-ink-muted hover:text-risk" aria-label="Remove trait" data-testid={`trait-remove-${i}`}
            onClick={() => onChange(traits.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="text-brand" data-testid="trait-add-btn"
        onClick={() => onChange([...traits, { trait: "New trait", min: 40, max: 80, importance: "medium" }])}>
        <Plus size={14} /> Add trait
      </Button>
    </div>
  );
}

export function KnockoutEditor({ questions = [], onChange }) {
  const update = (i, patch) => onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const outOfRange = questions.length < 3 || questions.length > 5;
  return (
    <div className="space-y-3" data-testid="knockout-editor">
      {questions.map((q, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`knockout-row-${i}`}>
          <Input value={q.question} onChange={(e) => update(i, { question: e.target.value })} className="h-9 flex-1 text-sm" data-testid={`knockout-question-${i}`} />
          <select value={q.expected_answer} onChange={(e) => update(i, { expected_answer: e.target.value })}
            className="h-9 rounded-lg border border-line bg-white px-2 text-sm" data-testid={`knockout-answer-${i}`} aria-label="Required answer">
            <option value="yes">Must answer yes</option><option value="no">Must answer no</option>
          </select>
          <button className="text-ink-muted hover:text-risk" aria-label="Remove question" data-testid={`knockout-remove-${i}`}
            onClick={() => onChange(questions.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="text-brand" disabled={questions.length >= 5} data-testid="knockout-add-btn"
          onClick={() => onChange([...questions, { question: "", expected_answer: "yes" }])}>
          <Plus size={14} /> Add question
        </Button>
        {outOfRange && <p className="text-xs text-caution" data-testid="knockout-count-warning">Use 3 to 5 questions.</p>}
      </div>
    </div>
  );
}

export function ListEditor({ items = [], onChange, testId, placeholder }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={it} onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))} className="h-9 text-sm" data-testid={`${testId}-item-${i}`} />
          <button className="text-ink-muted hover:text-risk" aria-label="Remove" onClick={() => onChange(items.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="text-brand" data-testid={`${testId}-add-btn`} onClick={() => onChange([...items, ""])}>
        <Plus size={14} /> {placeholder}
      </Button>
    </div>
  );
}
